import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AppFields } from "../components/apps/AppFields";
import { AppGrants } from "../components/apps/AppGrants";
import { AppKeys } from "../components/apps/AppKeys";
import { AppLimits } from "../components/apps/AppLimits";
import { AppPush } from "../components/apps/AppPush";
import { AppUsage } from "../components/apps/AppUsage";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { ErrorNotice, Notice } from "../components/ui/Feedback";
import { Pill } from "../components/ui/Status";
import { Datum, Metric, MicroLabel } from "../components/ui/Text";
import { get, patch } from "../lib/api";
import { dailyTotals } from "../lib/apps";
import { count, timestamp } from "../lib/format";
import { useSession } from "../lib/session";
import type { AppCatalog, AppDetail as Detail } from "../lib/types";

/**
 * One app: its keys, what it may read, how often, and what it has been doing.
 *
 * Disabling is the fast stop — every key refuses at once and nothing is lost —
 * where revoking is per key and final. Both are here because an incident wants
 * the first and a rotation wants the second.
 */

/** Where partners read the API's reference. Takes a key; served outside the panel. */
const DOCS_URL = "/docs/api/v1";

export function AppDetail() {
  const { appId = "" } = useParams();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);

  const detailKey = ["app", appId] as const;
  const detail = useQuery({
    queryKey: detailKey,
    queryFn: () => get<Detail>(`/apps/${encodeURIComponent(appId)}`),
    enabled: appId !== "",
  });
  const catalog = useQuery({
    queryKey: ["apps-catalog"],
    queryFn: () => get<AppCatalog>("/apps/catalog"),
    staleTime: Number.POSITIVE_INFINITY,
  });

  function changed(message: string) {
    setNotice(message);
    void queryClient.invalidateQueries({ queryKey: detailKey });
    void queryClient.invalidateQueries({ queryKey: ["apps"] });
  }

  const toggle = useMutation({
    mutationFn: (disabled: boolean) =>
      patch<Detail>(`/apps/${encodeURIComponent(appId)}`, { disabled }),
    onSuccess: (app) =>
      changed(
        app.disabled
          ? "App disabled. Every key is refused until it is enabled again."
          : "App enabled. Its live keys work again.",
      ),
  });

  const failed = detail.isError ? detail : catalog.isError ? catalog : undefined;
  if (failed !== undefined) {
    return (
      <>
        <PageHeader eyebrow="App" title="Not available" />
        <ErrorNotice error={failed.error} onRetry={() => void failed.refetch()} />
        <BackLink />
      </>
    );
  }

  const app = detail.data;
  const zone = catalog.data?.timeZone ?? "UTC";
  const today = app === undefined ? undefined : dailyTotals(app.usage)[0];
  const isToday =
    today !== undefined && Date.now() - new Date(today.day).getTime() < 24 * 60 * 60 * 1000;
  const canUpdate = can("apps.update");

  return (
    <>
      <PageHeader
        eyebrow="App"
        title={app?.name ?? "App"}
        metrics={
          app === undefined ? undefined : (
            <>
              <Metric
                label="Live keys"
                value={count(app.keys.filter((key) => key.status === "active").length)}
                hint={`${count(app.keys.length)} issued`}
              />
              <Metric label="Calls today" value={count(isToday ? today.allowed : 0)} />
              <Metric
                label="Refused today"
                value={count(isToday ? today.denied : 0)}
                tone={isToday && today.denied > 0 ? "signal" : "muted"}
              />
            </>
          )
        }
        lede={app?.description ?? undefined}
        actions={
          app === undefined ? undefined : (
            <div className="flex flex-wrap items-center gap-3">
              {app.disabled ? <Pill tone="faulted">Disabled</Pill> : null}
              {canUpdate && !isEditing ? (
                <Button variant="ghost" onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              ) : null}
              {canUpdate ? (
                <Button
                  variant={app.disabled ? "primary" : "danger"}
                  pending={toggle.isPending}
                  onClick={() => toggle.mutate(!app.disabled)}
                >
                  {app.disabled ? "Enable" : "Disable"}
                </Button>
              ) : null}
            </div>
          )
        }
      />

      {notice === undefined ? null : <Notice tone="ok">{notice}</Notice>}
      {toggle.isError ? <ErrorNotice error={toggle.error} /> : null}

      {app === undefined || catalog.data === undefined ? (
        <MicroLabel>Loading</MicroLabel>
      ) : (
        <>
          {isEditing ? (
            <AppFields
              heading="Edit app"
              submitLabel="Save"
              initial={app}
              submit={(body) => patch<Detail>(`/apps/${app.id}`, body)}
              onDone={(saved) => {
                setIsEditing(false);
                changed(`${saved.name} saved.`);
              }}
              onCancel={() => setIsEditing(false)}
            />
          ) : null}

          <dl className="m-0 grid grid-cols-1 gap-4 md:grid-cols-4">
            <Datum label="Acts for">
              {app.holder === null ? (
                "Every holder"
              ) : (
                <Link
                  to={`/holders/${encodeURIComponent(app.holder.id)}`}
                  className="text-signal underline decoration-1 underline-offset-4 hover:no-underline"
                >
                  {app.holder.name}
                </Link>
              )}
            </Datum>
            <Datum label="Created">{timestamp(app.createdAt, zone)}</Datum>
            <Datum label="Disabled">
              {app.disabledAt === null ? "—" : timestamp(app.disabledAt, zone)}
            </Datum>
            <Datum label="API reference">
              <a
                href={DOCS_URL}
                target="_blank"
                rel="noreferrer"
                className="text-signal underline decoration-1 underline-offset-4 hover:no-underline"
              >
                {DOCS_URL}
              </a>
            </Datum>
          </dl>

          <AppKeys
            app={app}
            timeZone={zone}
            canManage={can("apps.manage_keys")}
            onChanged={changed}
          />
          <AppGrants
            key={`grants-${app.permissions.join(",")}`}
            app={app}
            catalog={catalog.data}
            canEdit={canUpdate}
            onSaved={changed}
          />
          <AppLimits
            key={`limits-${JSON.stringify(app.limits)}`}
            app={app}
            catalog={catalog.data}
            canEdit={canUpdate}
            onSaved={changed}
          />
          <AppPush app={app} timeZone={zone} canEdit={canUpdate} onChanged={changed} />
          <AppUsage usage={app.usage} catalog={catalog.data} />
        </>
      )}

      <BackLink />
    </>
  );
}

function BackLink() {
  return (
    <Link
      to="/apps"
      className="text-data text-signal underline decoration-1 underline-offset-4 hover:no-underline"
    >
      All apps
    </Link>
  );
}
